import React, { useMemo } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { scaleQuantize } from "d3-scale";

// Deterministic pseudo-demand per state — corporate-looking hotspots
const STATE_DEMAND = {
  "01": 42, "02": 6,  "04": 58, "05": 22, "06": 92, "08": 61, "09": 24, "10": 18,
  "12": 74, "13": 66, "15": 4,  "16": 20, "17": 71, "18": 44, "19": 31, "20": 27,
  "21": 38, "22": 39, "23": 16, "24": 47, "25": 55, "26": 68, "27": 46, "28": 33,
  "29": 41, "30": 12, "31": 17, "32": 34, "33": 15, "34": 63, "35": 21, "36": 88,
  "37": 79, "38": 8,  "39": 62, "40": 43, "41": 51, "42": 72, "44": 12, "45": 48,
  "46": 10, "47": 57, "48": 96, "49": 32, "50": 8,  "51": 60, "53": 65, "54": 19,
  "55": 40, "56": 9,  "11": 28,
};

const COLORS = [
  "#F1F5F9", // slate-100 (very low)
  "#E2E8F0", // slate-200
  "#CBD5E1", // slate-300
  "#94A3B8", // slate-400
  "#EF9A9A", // red 200
  "#E57373", // red 300
  "#EF5350", // red 400
  "#C62828", // brand red
  "#B71C1C", // dark red (hottest)
];

const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const LABELS = [
  { code: "CA", val: 92 }, { code: "TX", val: 96 }, { code: "NY", val: 88 },
  { code: "FL", val: 74 }, { code: "IL", val: 71 }, { code: "PA", val: 72 },
  { code: "NC", val: 79 }, { code: "WA", val: 65 },
];

export default function USDemandMap({ height = 340, compact = false, title = "Contractor Demand · Trailing 30 Days" }) {
  const colorScale = useMemo(
    () => scaleQuantize().domain([0, 100]).range(COLORS),
    []
  );

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] font-mono-ll tracking-[0.24em] uppercase text-slate-500">{title}</div>
        <div className="flex items-center gap-1">
          <span className="text-[9px] font-mono-ll uppercase tracking-wider text-slate-400 mr-1">Low</span>
          {COLORS.map((c) => (
            <span key={c} className="w-3 h-3" style={{ background: c }}/>
          ))}
          <span className="text-[9px] font-mono-ll uppercase tracking-wider text-slate-400 ml-1">High</span>
        </div>
      </div>

      <div style={{ height: `${height}px`, position: "relative" }}>
        <ComposableMap
          projection="geoAlbersUsa"
          projectionConfig={{ scale: 950 }}
          style={{ width: "100%", height: "100%" }}
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const id = geo.id;
                const val = STATE_DEMAND[id] ?? 5;
                const fill = colorScale(val);
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fill}
                    stroke="#FFFFFF"
                    strokeWidth={0.75}
                    style={{
                      default: { outline: "none", transition: "fill 0.3s" },
                      hover:   { outline: "none", fill: "#17324D", cursor: "default" },
                      pressed: { outline: "none" },
                    }}
                  />
                );
              })
            }
          </Geographies>
        </ComposableMap>
      </div>

      {!compact && (
        <div className="mt-3 flex flex-wrap gap-2">
          {LABELS.map((l) => (
            <div key={l.code} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2 py-1">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: colorScale(l.val) }}/>
              <span className="text-[10px] font-mono-ll font-semibold text-[#17324D]">{l.code}</span>
              <span className="text-[10px] font-mono-ll text-slate-500">{l.val}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
